import type jsPDF from 'jspdf';
import type { Form9ClientParams } from './pdf-form9.js';
import { newPDF, placeImage, formatPDFDate, GREEN, type InspectionRow } from './pdf-layout.js';
import { FORM10_ICON_CERT_NO, FORM10_ICON_LICENSE_NO, FORM10_ICON_REGISTRATION_NO, FORM10_ICON_DATE, FORM10_ICON_SIG_USER, FORM10_ICON_COMPETENT_PERSON, FORM10_ICON_COMPETENCY_CERT, FORM10_ICON_VALIDITY, FORM10_ICON_ISSUING_AUTHORITY, FORM10_ICON_NEXT_INSPECTION } from './form10Assets.js';

// Form 9 image reference: native A4 text, green table and signature panels.
const LEFT = 9.74, RIGHT = 200.3, NUMBER_W = 9.8, LABEL_X = 21.5, VALUE_X = 114;
const DIVIDER_X = 111.8, TABLE_TOP = 57.8, PAGE_BOTTOM = 288.51;
const ORIGINAL_ROW_HEIGHTS = [8.2, 8.2, 12, 15.6, 68.4, 8, 12, 12, 8.3];
type Weight = 'normal' | 'semibold' | 'bold';
interface Line { text: string; size: number; weight: Weight; height: number }
interface Pair { label?: Line; value?: Line; height: number }

function wrap(doc: jsPDF, text: string, width: number, size: number, weight: Weight, height: number): Line[] {
  doc.setFont('Inter', weight); doc.setFontSize(size);
  return (doc.splitTextToSize((text || '-').replace(/[\u2010-\u2015]/g, '-'), width) as string[])
    .map(text => ({ text, size, weight, height }));
}
function paint(doc: jsPDF, line: Line, x: number, y: number) {
  doc.setFont('Inter', line.weight); doc.setFontSize(line.size);
  doc.text(line.text, x, y, { baseline: 'top' });
}
function text(doc: jsPDF, value: string, x: number, y: number, size: number, weight: Weight = 'normal') {
  paint(doc, { text: value, size, weight, height: 0 }, x, y);
}

/** Canva's design as native PDF text and paths, with content-aware row heights. */
export function renderForm9(data: Form9ClientParams, rows: InspectionRow[], statement: string): jsPDF {
  const doc = newPDF(`Form 9 - ${data.certificate_number}`);
  const person = data.personnel;
  const header = () => {
    doc.setTextColor(...GREEN);
    text(doc, 'Form No. 09', LEFT, 8.3, 28, 'bold');
    text(doc, '(Prescribed Under Rule 58)', LEFT, 20, 11);
    doc.setFillColor(...GREEN); doc.rect(LEFT, 27.66, 11.25, 0.79, 'F');
    doc.setTextColor(96, 160, 28);
    text(doc, 'Report Of Examination Of', LEFT, 36, 12, 'semibold');
    text(doc, 'Hoists And Lifts', LEFT, 42, 12, 'semibold');

    doc.setDrawColor(...GREEN); doc.setLineWidth(0.2); doc.line(86.5, 10, 86.5, 49.5);
    let y = 10.32;
    const metadata = [
      ['License Number', data.company?.license_number, FORM10_ICON_LICENSE_NO],
      ['Certificate No.', data.certificate_number, FORM10_ICON_CERT_NO],
      ['Registration Number', data.company?.registration_number, FORM10_ICON_REGISTRATION_NO],
      ['Date', formatPDFDate(data.examination_date), FORM10_ICON_DATE],
      ['NIC Code No.', data.company?.nic_code, FORM10_ICON_REGISTRATION_NO],
    ];
    metadata.forEach(([label, value, icon], i) => {
      const valueLines = wrap(doc, value || '-', RIGHT - 144 - 1, 8, i === 0 ? 'semibold' : 'normal', 3.4);
      const h = Math.max(8.54, valueLines.length * 3.4 + 2.5);
      if (i === 4) {
        doc.setFillColor(96, 160, 28); doc.circle(103.18, y + 3.1, 3.1, 'F');
        doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.16);
        for (let r = 0; r < 3; r++) for (let col = 0; col < 3; col++) doc.rect(101.55 + col * 1.2, y + 1.45 + r * 1.2, 0.8, 0.8);
      } else placeImage(doc, icon, 100.08, y, 6.2, 6.2);
      doc.setTextColor(...GREEN);
      text(doc, label || '', 108.45, y + 1.27, 8.3, 'semibold');
      text(doc, ':', 139.8, y + 1.27, 8.3, 'semibold');
      doc.setTextColor(0, 0, 0);
      valueLines.forEach((line, j) => paint(doc, line, 144, y + 1.27 + j * 3.4));
      if (i < 4) { doc.setDrawColor(...GREEN); doc.setLineWidth(0.15); doc.line(100.08, y + h - 1.3, RIGHT, y + h - 1.3); }
      y += h;
    });
    return Math.max(TABLE_TOP, y + 4.5);
  };
  let y = header();
  const measured = rows.map(row => {
    let labels = row.label.split(/\n(?=\([A-L]\))/);
    let values = row.value.split(/\n(?=\([A-L]\))/);
    const pairs: Pair[] = [];
    const paired = labels.length > 1;
    for (let partIndex = 0; partIndex < labels.length; partIndex++) {
      const label = labels[partIndex];
      const part = /^\([A-L]\)/.test(label);
      const labelLines = wrap(doc, label, DIVIDER_X - LABEL_X - 1.5, part ? 8 : 8.3, part ? 'normal' : 'semibold', part ? 3.65 : 3.75);
      const value = paired ? values[partIndex] || '-' : row.value;
      const valueLines = wrap(doc, value, RIGHT - VALUE_X - 2, 8.3, 'normal', 3.75);
      for (let i = 0; i < Math.max(labelLines.length, valueLines.length); i++) {
        pairs.push({ label: labelLines[i], value: valueLines[i], height: Math.max(labelLines[i]?.height || 0, valueLines[i]?.height || 0) });
      }
    }
    return { ...row, pairs, contentH: pairs.reduce((sum, pair) => sum + pair.height, 0) };
  });
  const cert = wrap(doc, statement, RIGHT - LEFT, 9.5, 'normal', 4.2);
  const detailIcons = [FORM10_ICON_COMPETENT_PERSON, FORM10_ICON_COMPETENCY_CERT, FORM10_ICON_VALIDITY, FORM10_ICON_ISSUING_AUTHORITY, FORM10_ICON_NEXT_INSPECTION];
  const details = [
    ['Name of Competent Person', person?.full_name],
    ['Competency Certificate No.', person?.competency_cert_number],
    ['Validity', [formatPDFDate(person?.competency_valid_from), formatPDFDate(person?.competency_valid_upto)].filter(Boolean).join(' to ')],
    ['Certificate issued by', person?.issuing_authority],
    ['Next Inspection Date', formatPDFDate(data.next_inspection_date)],
  ].map(([label, value]) => ({ label: label || '', lines: wrap(doc, value || '-', 77, 8, 'normal', 3.45) }));
  const detailHeights = details.map(row => Math.max(11.064, row.lines.length * 3.45 + 3));
  const cardH = Math.max(55.32, detailHeights.reduce((sum, h) => sum + h, 0));
  const closingH = 4 + cert.length * 4.2 + 4 + cardH;
  const available = PAGE_BOTTOM - y - closingH;
  let padding = 0.8;
  const minimumTotal = () => measured.reduce((sum, row) => sum + row.contentH + padding * 2, 0);
  if (minimumTotal() > available) padding = 0.5;
  const compactTotal = minimumTotal();
  const desired = measured.map((row, i) => Math.max(ORIGINAL_ROW_HEIGHTS[i], row.contentH + padding * 2));
  const desiredTotal = desired.reduce((sum, h) => sum + h, 0);
  const fitsOnePage = compactTotal <= available;
  const extraRatio = desiredTotal <= available ? 1 : fitsOnePage ? (available - compactTotal) / (desiredTotal - compactTotal) : 1;
  const nextPage = () => { doc.addPage(); y = header(); };
  const drawPart = (row: typeof measured[number], pairs: Pair[], h: number) => {
    doc.setFillColor(...GREEN); doc.rect(LEFT, y, NUMBER_W, h, 'F');
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.18);
    doc.rect(LEFT + NUMBER_W, y, RIGHT - LEFT - NUMBER_W, h);
    doc.line(DIVIDER_X, y, DIVIDER_X, y + h);
    doc.setDrawColor(255, 255, 255); doc.line(LEFT, y + h, LEFT + NUMBER_W, y + h);
    doc.setTextColor(255, 255, 255); doc.setFont('Inter', 'bold'); doc.setFontSize(7);
    doc.text(row.number, LEFT + NUMBER_W / 2, y + h / 2, { align: 'center', baseline: 'middle' });
    doc.setTextColor(...GREEN);
    let lineY = y + padding;
    for (const pair of pairs) {
      doc.setTextColor(...GREEN);
      if (pair.label) paint(doc, pair.label, LABEL_X, lineY);
      doc.setTextColor(0, 0, 0);
      if (pair.value) paint(doc, pair.value, VALUE_X, lineY);
      lineY += pair.height;
    }
    y += h;
  };
  measured.forEach((row, index) => {
    const natural = row.contentH + padding * 2;
    const height = natural + (desired[index] - natural) * extraRatio;
    if (fitsOnePage || y + height <= 282) { drawPart(row, row.pairs, height); return; }
    if (height <= 45) { nextPage(); drawPart(row, row.pairs, height); return; }
    let offset = 0;
    while (offset < row.pairs.length) {
      let count = 0, h = padding * 2;
      while (offset + count < row.pairs.length && y + h + row.pairs[offset + count].height <= 282) {
        h += row.pairs[offset + count].height; count++;
      }
      if (!count) { nextPage(); continue; }
      drawPart(row, row.pairs.slice(offset, offset + count), h);
      offset += count;
      if (offset < row.pairs.length) nextPage();
    }
  });
  if (y + closingH > PAGE_BOTTOM + 0.01) nextPage();
  const singlePage = doc.getNumberOfPages() === 1;
  const cardY = singlePage ? Math.max(y + 8 + cert.length * 4.2, PAGE_BOTTOM - cardH) : y + 8 + cert.length * 4.2;
  const certY = singlePage ? Math.max(y + 4, Math.min(222.5, cardY - 4 - cert.length * 4.2)) : y + 4;
  doc.setTextColor(0, 0, 0);
  cert.forEach((line, i) => paint(doc, line, LEFT, certY + i * 4.2));
  doc.setFillColor(244, 248, 239); doc.setDrawColor(...GREEN); doc.setLineWidth(0.25);
  doc.roundedRect(9.74, cardY, 49.84, cardH, 3, 3, 'FD');
  doc.roundedRect(61.99, cardY, 138.72, cardH, 3, 3, 'FD');
  placeImage(doc, FORM10_ICON_SIG_USER, 12.96, cardY + 3.17, 7.62, 7.62);
  doc.setTextColor(...GREEN);
  text(doc, 'Signature of', 22.45, cardY + 3.28, 8, 'semibold');
  text(doc, 'Competent Person', 22.45, cardY + 7, 8, 'semibold');
  placeImage(doc, data.personnel_signature_data_url, 13, cardY + 15, 42, 13);
  placeImage(doc, data.personnel_stamp_data_url, 25, cardY + 29, 20, 18);
  let detailY = cardY;
  details.forEach((detail, i) => {
    placeImage(doc, detailIcons[i], 64.98, detailY + 1.64, 6.26, 6.26);
    doc.setTextColor(...GREEN);
    text(doc, detail.label, 72.78, detailY + 2.92, 8.1, 'semibold');
    text(doc, ':', 116.15, detailY + 2.92, 8.1, 'semibold');
    doc.setTextColor(0, 0, 0);
    detail.lines.forEach((line, j) => paint(doc, line, 120, detailY + 2.92 + j * 3.45));
    detailY += detailHeights[i];
    if (i < 4) { doc.setDrawColor(...GREEN); doc.setLineWidth(0.15); doc.line(64.98, detailY, 197.43, detailY); }
  });
  for (let page = 1; page <= doc.getNumberOfPages(); page++) {
    doc.setPage(page);
    // Inset the Canva footer band so an ordinary A4 printer can reproduce it.
    doc.setFillColor(96, 160, 28); doc.rect(LEFT, 290, RIGHT - LEFT, 2, 'F');
    doc.setFillColor(...GREEN); doc.rect(LEFT, 290, 136, 2, 'F');
    doc.triangle(LEFT + 136, 290, LEFT + 140, 290, LEFT + 138, 292, 'F');
    if (doc.getNumberOfPages() > 1) {
      doc.setTextColor(...GREEN); text(doc, `Page ${page} / ${doc.getNumberOfPages()}${page > 1 ? ' - continued' : ''}`, LEFT, 53, 6);
    }
  }
  return doc;
}
